import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Checkbox, Divider, Icon, Message, Dropdown } from 'semantic-ui-react';
import { TeamListDto, TeamContractRules, defaultContractRules } from '../../services/teams/teamService';
import { fastchipClusterSettingsService } from '../../services/fastchipClusterSettings/FastchipClusterSettingsService';

interface RuleConfig {
  key: Exclude<keyof TeamContractRules, 'rjMgEsFastchipClusterPreference'>;
  label: string;
  description: string;
}

const RULES_CONFIG: RuleConfig[] = [
  {
    key: 'requireAditivosForSuspendedLines',
    label: 'Exigir aditivos para linhas suspensas',
    description:
      'Para cada 2 linhas marcadas para suspensão, é obrigatório incluir 1 linha aditiva. ' +
      'Quando desativado, contratos deste time podem ter renegociações sem a quantidade mínima de aditivos.',
  },
  {
    key: 'requireSeraUsadaPeloCliente',
    label: 'Exigir informação "Será usada pelo cliente" em linhas aditivas',
    description:
      'Linhas do tipo Aditivo devem ter o campo "Será usada pelo cliente" preenchido antes de salvar o contrato.',
  },
  {
    key: 'requirePortabilidadeCompleta',
    label: 'Exigir campos completos de portabilidade',
    description:
      'Quando uma linha é marcada como portabilidade: operadora e linha a portar são obrigatórias; nome e CPF devem ser preenchidos juntos (PF→CNPJ) ou deixados em branco (CNPJ→CNPJ).',
  },
  {
    key: 'blockDuplicateCcid',
    label: 'Bloquear CCIDs duplicados em linhas aditivas',
    description:
      'Impede a adição de duas linhas Aditivo com o mesmo CCID (chip) no mesmo contrato.',
  },
  {
    key: 'blockDuplicatePhone',
    label: 'Bloquear telefones duplicados em renegociações',
    description:
      'Impede a adição de duas linhas de Renegociação com o mesmo número de telefone no mesmo contrato.',
  },
  {
    key: 'requireClienteAtivo',
    label: 'Exigir situação cadastral ATIVA do cliente',
    description:
      'O cliente deve estar com situação cadastral ATIVA (Receita Federal) para que um contrato possa ser criado.',
  },
  {
    key: 'allowContractDraft',
    label: 'Permitir rascunho automático de contrato',
    description:
      'Quando habilitado, o progresso da criação de contratos é salvo automaticamente como rascunho ao preencher linhas e navegar entre abas. ' +
      'Quando desabilitado, nenhum rascunho novo é criado ou atualizado automaticamente — o contrato só é salvo ao clicar em Salvar Contrato na última aba. ' +
      'Rascunhos já existentes podem ser continuados e finalizados, mas não serão mais atualizados automaticamente.',
  },
  {
    key: 'allowContractLineEdit',
    label: 'Permitir edição de linhas após criação do contrato',
    description:
      'Quando habilitado, membros do time podem editar linhas no modal de contratos que ainda não passaram pelas etapas que bloqueiam edição. ' +
      'Quando desabilitado, nenhum membro pode editar linhas em qualquer momento — inclusive quem possui bypass individual no cadastro.',
  },
];

interface Props {
  open: boolean;
  team: TeamListDto | null;
  onClose: () => void;
  onSave: (rules: TeamContractRules) => Promise<void>;
}

const TeamRulesModal: React.FC<Props> = ({ open, team, onClose, onSave }) => {
  const [rules, setRules] = useState<TeamContractRules>(defaultContractRules);
  const [saving, setSaving] = useState(false);
  const [bothClustersEnabled, setBothClustersEnabled] = useState(false);

  useEffect(() => {
    if (team) {
      setRules({ ...defaultContractRules, ...team.contractRules });
    }
  }, [team]);

  useEffect(() => {
    if (!open) return;
    fastchipClusterSettingsService
      .getSettings()
      .then((settings) => setBothClustersEnabled(settings.er2Enabled && settings.dhEnabled))
      .catch(() => setBothClustersEnabled(false));
  }, [open]);

  const handleToggle = (key: keyof TeamContractRules) => {
    setRules(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(rules);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setRules(defaultContractRules);
  };

  const anyRuleDisabled = RULES_CONFIG.some(r => !rules[r.key]);

  return (
    <Modal open={open} onClose={onClose} size="small" closeIcon>
      <Modal.Header
        style={{
          background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)',
          borderBottom: '3px solid #4A90E2',
        }}
      >
        <Icon name="settings" style={{ color: '#2c3e50' }} />
        Regras de Contrato — {team?.name}
      </Modal.Header>

      <Modal.Content scrolling>
        {anyRuleDisabled && (
          <Message warning icon>
            <Icon name="warning sign" />
            <Message.Content>
              <Message.Header>Regras desativadas</Message.Header>
              Este time possui regras desativadas. Certifique-se de que isto é intencional.
            </Message.Content>
          </Message>
        )}

        <p style={{ color: '#555', marginBottom: '20px' }}>
          Ative ou desative as regras de validação que se aplicam à criação e edição de contratos para o time{' '}
          <strong>{team?.name}</strong>. As regras desativadas não serão verificadas para os membros deste time.
        </p>

        <Form>
          {RULES_CONFIG.map((rule, idx) => (
            <React.Fragment key={rule.key}>
              {idx > 0 && <Divider style={{ margin: '12px 0' }} />}
              <Form.Field>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                  <Checkbox
                    toggle
                    checked={rules[rule.key]}
                    onChange={() => handleToggle(rule.key)}
                    style={{ flexShrink: 0, marginTop: '2px' }}
                  />
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        color: rules[rule.key] ? '#2c3e50' : '#999',
                        marginBottom: '4px',
                      }}
                    >
                      {rule.label}
                    </div>
                    <div style={{ fontSize: '0.85em', color: '#777', lineHeight: '1.4' }}>
                      {rule.description}
                    </div>
                  </div>
                </div>
              </Form.Field>
            </React.Fragment>
          ))}

          <Divider style={{ margin: '12px 0' }} />
          <Form.Field>
            <label>Preferência de cluster Fastchip RJ/MG/ES</label>
            <Dropdown
              selection
              disabled={!bothClustersEnabled}
              options={[
                { key: 'auto', text: 'Automático (usa o cluster padrão do admin)', value: '' },
                { key: 'ER2', text: 'E.R.2', value: 'ER2' },
                { key: 'DH', text: 'DH', value: 'DH' },
              ]}
              value={rules.rjMgEsFastchipClusterPreference ?? ''}
              onChange={(_, { value }) =>
                setRules((prev) => ({
                  ...prev,
                  rjMgEsFastchipClusterPreference: (value as 'ER2' | 'DH' | '') || null,
                }))
              }
            />
            <div style={{ fontSize: '0.85em', color: '#777', marginTop: '6px', lineHeight: '1.4' }}>
              {bothClustersEnabled
                ? 'Define qual cluster (E.R.2 ou DH) este time usa quando o vendedor cadastra uma linha Fastchip no mercado RJ/MG/ES. Um contrato nunca mistura os dois.'
                : 'Só um cluster está habilitado globalmente no momento — a preferência do time é ignorada até o admin habilitar os dois em Gestão de Acessos → Clusters Fastchip.'}
            </div>
          </Form.Field>
        </Form>
      </Modal.Content>

      <Modal.Actions>
        <Button basic onClick={handleReset} title="Restaurar todas as regras padrão">
          <Icon name="undo" />
          Restaurar padrões
        </Button>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="blue" onClick={handleSave} loading={saving} disabled={saving}>
          <Icon name="save" />
          Salvar regras
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default TeamRulesModal;
